import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, ListOrdered, PenLine, GripVertical } from "lucide-react";
import { useListBoq, useListBudget } from "@workspace/api-client-react";
import type { TaxTypeWithRates, BoqItem, BudgetLine } from "@workspace/api-client-react";

export type LineItem = {
  id: string;
  description: string;
  note?: string;           // manual override / extra description (for BOQ/budget items)
  unit: string;
  qty: number;
  rate: number;
  amount: number;
  taxTypeId: string | null;
  taxTypeName: string | null;
  taxRate: number;
  taxAmount: number;
  sourceType: "free" | "boq" | "budget";
  sourceId: string | null;
};

const N = (v: unknown): number => { const n = Number(v); return isNaN(n) ? 0 : n; };
const fc = (v: number) => v.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function recalc(item: LineItem): LineItem {
  const amount = N(item.qty) * N(item.rate);
  return { ...item, amount, taxAmount: amount * N(item.taxRate) };
}

interface LineItemsEditorProps {
  projectId: string;
  value: LineItem[];
  onChange: (items: LineItem[]) => void;
  sourceMode?: "free" | "boq" | "budget";
  onSourceModeChange?: (mode: "free" | "boq" | "budget") => void;
  availableModes?: ("free" | "boq" | "budget")[];
  taxTypes: TaxTypeWithRates[];
  taxLabel: string;
  disabled?: boolean;
}

export function LineItemsEditor({
  projectId, value, onChange,
  availableModes = ["free", "boq", "budget"],
  taxTypes, taxLabel, disabled = false,
}: LineItemsEditorProps) {
  const [boqOpen, setBoqOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const { data: boqItems = [] } = useListBoq({ projectId });
  const { data: budgetLines = [] } = useListBudget({ projectId });

  const canBoq    = availableModes.includes("boq");
  const canBudget = availableModes.includes("budget");

  const defaultTax = () => {
    const t = taxTypes[0] ?? null;
    const r = t?.rates.find(r => r.isDefault) ?? t?.rates[0] ?? null;
    return { taxTypeId: t?.id ?? null, taxTypeName: t?.name ?? null, taxRate: r ? N(r.rate) : 0 };
  };

  const addFreeRow = () => {
    const tax = defaultTax();
    onChange([...value, recalc({
      id: `li-${Date.now()}`, description: "", unit: "", qty: 1, rate: 0, amount: 0,
      ...tax, taxAmount: 0, sourceType: "free", sourceId: null,
    })]);
  };

  const update = (id: string, patch: Partial<LineItem>) =>
    onChange(value.map(it => it.id === id ? recalc({ ...it, ...patch }) : it));

  const remove = (id: string) => onChange(value.filter(it => it.id !== id));

  const onTaxType = (id: string, typeId: string) => {
    if (typeId === "__none__") { update(id, { taxTypeId: null, taxTypeName: null, taxRate: 0 }); return; }
    const t = taxTypes.find(tt => tt.id === typeId);
    if (!t) return;
    const r = t.rates.find(rr => rr.isDefault) ?? t.rates[0] ?? null;
    update(id, { taxTypeId: t.id, taxTypeName: t.name, taxRate: r ? N(r.rate) : 0 });
  };

  const addBoqItem = (b: BoqItem) => {
    const tax = defaultTax();
    const qty = N(b.qty), rate = N(b.rate), amount = qty * rate;
    onChange([...value, {
      id: `li-${Date.now()}`, description: b.itm || b.sec || "", unit: b.unit ?? "",
      qty, rate, amount, ...tax, taxAmount: amount * tax.taxRate, sourceType: "boq", sourceId: b.id,
    }]);
  };

  const addBudgetItem = (b: BudgetLine) => {
    const tax = defaultTax();
    const amount = N(b.appBgt);
    onChange([...value, {
      id: `li-${Date.now()}`, description: b.desc || b.wbs || "", unit: "",
      qty: 1, rate: amount, amount, ...tax, taxAmount: amount * tax.taxRate, sourceType: "budget", sourceId: b.id,
    }]);
  };

  const netTotal = value.reduce((s, i) => s + i.amount, 0);
  const taxTotal = value.reduce((s, i) => s + i.taxAmount, 0);

  const SOURCE_STYLE = {
    free:   { label: "Free",   cls: "bg-muted text-muted-foreground" },
    boq:    { label: "BOQ",    cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" },
    budget: { label: "Budget", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" },
  };

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Line Items {value.length > 0 && <span className="ml-1 normal-case font-normal">({value.length})</span>}
        </p>
        {!disabled && (
          <div className="flex gap-1.5">
            {canBoq && (
              <Button type="button" variant="outline" size="sm"
                className="h-7 text-xs rounded-lg px-3 gap-1.5 text-blue-700 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-950/40"
                onClick={() => setBoqOpen(true)}>
                <ListOrdered className="w-3.5 h-3.5" />From BOQ
              </Button>
            )}
            {canBudget && (
              <Button type="button" variant="outline" size="sm"
                className="h-7 text-xs rounded-lg px-3 gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-800 dark:hover:bg-amber-950/40"
                onClick={() => setBudgetOpen(true)}>
                <ListOrdered className="w-3.5 h-3.5" />From Budget
              </Button>
            )}
            <Button type="button" variant="outline" size="sm"
              className="h-7 text-xs rounded-lg px-3 gap-1.5"
              onClick={addFreeRow}>
              <PenLine className="w-3.5 h-3.5" />Add Row
            </Button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {value.length === 0 ? (
        <div className="border-2 border-dashed border-border/50 rounded-xl py-8 text-center space-y-1.5 bg-muted/20">
          <p className="text-sm font-medium text-muted-foreground">No items yet</p>
          <p className="text-xs text-muted-foreground/70">
            {canBoq ? 'Click "From BOQ" to link BOQ items, or "Add Row" for free entry.' :
             canBudget ? 'Click "From Budget" to link budget lines, or "Add Row" for free entry.' :
             'Click "Add Row" to begin.'}
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Column header */}
          <div className="grid items-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-3"
            style={{ gridTemplateColumns: "1fr 60px 60px 90px 100px 140px 90px 28px" }}>
            <span>Description</span>
            <span className="text-center">Unit</span>
            <span className="text-right">Qty</span>
            <span className="text-right">Rate</span>
            <span className="text-right">Amount</span>
            <span>{taxLabel} Type</span>
            <span className="text-right">{taxLabel} Amt</span>
            <span />
          </div>

          {/* Rows */}
          {value.map((item, idx) => {
            const src = SOURCE_STYLE[item.sourceType];
            const taxRates = item.taxTypeId ? (taxTypes.find(t => t.id === item.taxTypeId)?.rates ?? []) : [];
            const hasSource = item.sourceType === "boq" || item.sourceType === "budget";
            return (
              <div key={item.id}
                className="group bg-card border border-border/60 rounded-xl px-3 py-2 hover:border-border hover:shadow-sm transition-all space-y-1">
                {/* Main row */}
                <div className="grid items-center gap-1"
                  style={{ gridTemplateColumns: "1fr 60px 60px 90px 100px 140px 90px 28px" }}>

                {/* Description + source badge */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wide ${src.cls}`}>
                    {src.label}
                  </span>
                  <Input
                    disabled={disabled}
                    value={item.description}
                    onChange={e => update(item.id, { description: e.target.value })}
                    placeholder={`Item ${idx + 1} description…`}
                    className="h-7 text-xs border-0 bg-transparent shadow-none focus-visible:ring-0 px-0 flex-1"
                  />
                </div>

                {/* Unit */}
                <Input
                  disabled={disabled}
                  value={item.unit}
                  onChange={e => update(item.id, { unit: e.target.value })}
                  placeholder="unit"
                  className="h-7 text-xs text-center bg-muted/40 border-0 rounded-lg shadow-none focus-visible:ring-1 focus-visible:ring-primary/30 px-1"
                />

                {/* Qty */}
                <Input
                  disabled={disabled}
                  type="number"
                  value={item.qty === 0 ? "" : item.qty}
                  onChange={e => update(item.id, { qty: N(e.target.value) })}
                  placeholder="0"
                  className="h-7 text-xs text-right bg-muted/40 border-0 rounded-lg shadow-none focus-visible:ring-1 focus-visible:ring-primary/30 px-2"
                />

                {/* Rate */}
                <Input
                  disabled={disabled}
                  type="number"
                  value={item.rate === 0 ? "" : item.rate}
                  onChange={e => update(item.id, { rate: N(e.target.value) })}
                  placeholder="0.00"
                  className="h-7 text-xs text-right bg-muted/40 border-0 rounded-lg shadow-none focus-visible:ring-1 focus-visible:ring-primary/30 px-2"
                />

                {/* Amount (read-only) */}
                <div className="text-right text-xs font-semibold tabular-nums px-1">
                  {fc(item.amount)}
                </div>

                {/* Tax type — compact */}
                <div className="flex items-center gap-1">
                  {taxTypes.length > 0 ? (
                    <>
                      <Select disabled={disabled} value={item.taxTypeId ?? "__none__"} onValueChange={v => onTaxType(item.id, v)}>
                        <SelectTrigger className="h-7 text-xs bg-muted/40 border-0 rounded-lg shadow-none focus:ring-1 focus:ring-primary/30 flex-1 min-w-0">
                          <SelectValue placeholder="No tax" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No tax</SelectItem>
                          {taxTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {item.taxTypeId && taxRates.length > 1 ? (
                        <Select disabled={disabled} value={String(item.taxRate)}
                          onValueChange={v => update(item.id, { taxRate: Number(v), taxAmount: item.amount * Number(v) })}>
                          <SelectTrigger className="h-7 text-xs bg-muted/40 border-0 rounded-lg shadow-none focus:ring-1 focus:ring-primary/30 w-14 shrink-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {taxRates.map(r => (
                              <SelectItem key={r.id} value={String(Number(r.rate))}>
                                {(Number(r.rate) * 100).toFixed(0)}%
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : item.taxTypeId ? (
                        <span className="text-[10px] text-muted-foreground shrink-0 w-10 text-right">
                          {(item.taxRate * 100).toFixed(0)}%
                        </span>
                      ) : null}
                    </>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </div>

                {/* Tax amount */}
                <div className="text-right text-xs tabular-nums text-muted-foreground px-1">
                  {item.taxTypeId ? fc(item.taxAmount) : "—"}
                </div>

                {/* Delete */}
                <div className="flex justify-center">
                  {!disabled && (
                    <button type="button" onClick={() => remove(item.id)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-1 rounded-lg transition-opacity">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                </div>

                {hasSource && (
                  <div className="flex items-center gap-2 pl-1">
                    <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide shrink-0 w-14">Note</span>
                    <Input
                      disabled={disabled}
                      value={item.note ?? ""}
                      onChange={e => update(item.id, { note: e.target.value })}
                      placeholder="Add custom note / description override for this item…"
                      className="h-6 text-[11px] border-0 bg-muted/30 shadow-none focus-visible:ring-1 focus-visible:ring-primary/30 rounded-md px-2 flex-1 italic placeholder:not-italic"
                    />
                  </div>
                )}
              </div>
            );
          })}

          {/* Totals */}
          <div className="border-t border-border/60 pt-2 mt-1 space-y-1 px-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Net Total <span className="text-[10px]">(excl. {taxLabel})</span></span>
              <span className="tabular-nums font-medium text-foreground">{fc(netTotal)}</span>
            </div>
            {taxTotal > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{taxLabel}</span>
                <span className="tabular-nums">{fc(taxTotal)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-bold border-t border-border/60 pt-1.5 mt-1">
              <span>Gross Total <span className="text-xs font-normal text-muted-foreground">(incl. {taxLabel})</span></span>
              <span className="tabular-nums">{fc(netTotal + taxTotal)}</span>
            </div>
          </div>
        </div>
      )}

      {/* BOQ picker dialog */}
      <Dialog open={boqOpen} onOpenChange={setBoqOpen}>
        <DialogContent className="max-w-full sm:max-w-[680px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Pick from BOQ</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[440px] -mx-2">
            {boqItems.length === 0 ? (
              <div className="text-center py-12 space-y-2">
                <p className="text-sm font-semibold">No BOQ items found</p>
                <p className="text-xs text-muted-foreground">Add items in the project's BOQ tab first.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="border-b border-border text-xs text-muted-foreground font-semibold">
                    <th className="text-left px-4 py-2.5">Section / Item</th>
                    <th className="text-right px-3 py-2.5 w-16">Qty</th>
                    <th className="text-left px-2 py-2.5 w-12">Unit</th>
                    <th className="text-right px-3 py-2.5 w-24">Rate</th>
                    <th className="text-right px-4 py-2.5 w-28">Amount</th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <tbody>
                  {(boqItems as BoqItem[]).map(b => (
                    <tr key={b.id} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2.5">
                        {b.sec && <span className="text-muted-foreground text-xs mr-1.5">{b.sec} /</span>}
                        <span className="font-medium">{b.itm}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{b.qty}</td>
                      <td className="px-2 py-2.5 text-muted-foreground text-xs">{b.unit}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fc(N(b.rate))}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fc(N(b.qty) * N(b.rate))}</td>
                      <td className="px-3 py-2.5 text-right">
                        <Button type="button" size="sm" onClick={() => addBoqItem(b)}
                          className="h-7 text-xs rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3">
                          + Add
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="flex justify-end border-t border-border pt-3">
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setBoqOpen(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Budget picker dialog */}
      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent className="max-w-full sm:max-w-[560px] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Pick from Budget</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[440px] -mx-2">
            {budgetLines.length === 0 ? (
              <div className="text-center py-12 space-y-2">
                <p className="text-sm font-semibold">No budget lines found</p>
                <p className="text-xs text-muted-foreground">Add lines in the project's Budget tab first.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="border-b border-border text-xs text-muted-foreground font-semibold">
                    <th className="text-left px-4 py-2.5 w-20">Code</th>
                    <th className="text-left px-3 py-2.5">Description</th>
                    <th className="text-right px-4 py-2.5 w-32">Budget</th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <tbody>
                  {(budgetLines as BudgetLine[]).map(b => (
                    <tr key={b.id} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{b.wbs}</td>
                      <td className="px-3 py-2.5 font-medium">{b.desc}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fc(N(b.appBgt))}</td>
                      <td className="px-3 py-2.5 text-right">
                        <Button type="button" size="sm" onClick={() => addBudgetItem(b)}
                          className="h-7 text-xs rounded-lg bg-amber-600 hover:bg-amber-700 text-white px-3">
                          + Add
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="flex justify-end border-t border-border pt-3">
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setBudgetOpen(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function lineItemsNetTotal(items: LineItem[]): number {
  return items.reduce((s, i) => s + i.amount, 0);
}

export function lineItemsTaxTotal(items: LineItem[]): number {
  return items.reduce((s, i) => s + i.taxAmount, 0);
}
